import { baseApi } from './baseApi';
import { normalizeKeys } from '../../utils/transform';

/* ---------- types ---------- */

export interface WorkspaceInputResponsePayload {
  requestId: string;
  answer: unknown;
}

export interface WorkspaceChatRequest {
  query: string;
  sessionId?: string;
  projectId?: string;
  model?: string;
  interactionMode?: 'auto' | 'ask' | 'explore' | 'build';
  /** Structured response to a pending agent_input_request. */
  inputResponse?: WorkspaceInputResponsePayload;
}

export interface WorkspaceChatResponse {
  sessionId: string;
  response?: string;
  conversationId?: string;
  agentName?: string;
  exchangeId?: string;
  status?: 'processing' | 'completed' | 'failed';
}

export interface WorkspaceProject {
  id: string;
  name: string;
  color: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateProjectRequest {
  name: string;
  color?: string;
}

export interface UpdateProjectRequest {
  id: string;
  name?: string;
  color?: string;
}

export interface WorkspaceSession {
  id: string;
  sessionId: string;
  projectId: string | null;
  title: string;
  status: 'active' | 'completed' | 'archived';
  activeTab: 'explore' | 'analyze' | 'build';
  createdAt: string;
  updatedAt: string;
}

export interface WorkspaceSessionDetail extends WorkspaceSession {
  plans: WorkspacePlan[];
  artefacts: WorkspaceArtefact[];
}

export interface WorkspacePlanStep {
  stepId: string;
  sequence: number;
  type: string;
  title: string;
  description: string;
  agentName?: string;
  jobType?: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  estimatedDurationSeconds?: number;
  actualDurationSeconds?: number;
  resultSummary?: string;
  error?: string;
}

export interface WorkspacePlan {
  planId: string;
  title: string;
  status: 'draft' | 'approved' | 'executing' | 'completed' | 'failed' | 'cancelled';
  steps: WorkspacePlanStep[];
  estimatedDurationSeconds?: number;
  actualDurationSeconds?: number;
  createdAt: string;
}

export interface WorkspaceArtefact {
  id: string;
  title: string;
  type: 'html' | 'image' | 'app' | 'url';
  shareScope: 'private' | 'org' | 'public';
  shareToken?: string;
  presignedUrl?: string;
  metadataJson?: Record<string, unknown>;
  createdAt: string;
}

export interface ShareArtefactRequest {
  id: string;
  shareScope: 'private' | 'org' | 'public';
}

/* ---------- api ---------- */

export const workspaceApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    sendWorkspaceMessage: builder.mutation<WorkspaceChatResponse, WorkspaceChatRequest>({
      query: (body) => ({
        url: '/workspace/chat',
        method: 'POST',
        body: {
          query: body.query,
          ...(body.sessionId && { 'session-id': body.sessionId }),
          ...(body.projectId && { 'project-id': body.projectId }),
          ...(body.model && { model: body.model }),
          interaction_mode: body.interactionMode ?? 'auto',
          ...(body.inputResponse && {
            input_response: {
              request_id: body.inputResponse.requestId,
              answer: body.inputResponse.answer,
            },
          }),
        },
      }),
      transformResponse: (raw: unknown) =>
        normalizeKeys<WorkspaceChatResponse>(raw),
    }),

    getProjects: builder.query<WorkspaceProject[], void>({
      query: () => '/workspace/projects',
      transformResponse: (raw: unknown) => {
        const normalized = normalizeKeys<{ projects: WorkspaceProject[] }>(raw);
        return normalized.projects ?? [];
      },
      providesTags: ['WorkspaceProject'],
    }),

    createProject: builder.mutation<WorkspaceProject, CreateProjectRequest>({
      query: (body) => ({
        url: '/workspace/projects',
        method: 'POST',
        body,
      }),
      transformResponse: (raw: unknown) =>
        normalizeKeys<WorkspaceProject>(raw),
      invalidatesTags: ['WorkspaceProject'],
    }),

    updateProject: builder.mutation<WorkspaceProject, UpdateProjectRequest>({
      query: ({ id, ...body }) => ({
        url: `/workspace/projects/${id}`,
        method: 'PUT',
        body,
      }),
      transformResponse: (raw: unknown) =>
        normalizeKeys<WorkspaceProject>(raw),
      invalidatesTags: ['WorkspaceProject'],
    }),

    deleteProject: builder.mutation<void, string>({
      query: (id) => ({
        url: `/workspace/projects/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['WorkspaceProject'],
    }),

    getWorkspaceSessions: builder.query<
      WorkspaceSession[],
      { projectId?: string } | void
    >({
      query: (params) => ({
        url: '/workspace/sessions',
        params: params
          ? { ...(params.projectId && { 'project-id': params.projectId }) }
          : undefined,
      }),
      transformResponse: (raw: unknown) => {
        const normalized = normalizeKeys<{ sessions: WorkspaceSession[] }>(raw);
        return normalized.sessions ?? [];
      },
      providesTags: ['Workspace'],
    }),

    getSessionDetail: builder.query<WorkspaceSessionDetail, string>({
      query: (id) => `/workspace/sessions/${id}`,
      transformResponse: (raw: unknown) => {
        const normalized = normalizeKeys<{
          session: WorkspaceSession;
          plans: WorkspacePlan[];
          artefacts: WorkspaceArtefact[];
        }>(raw);
        return {
          ...normalized.session,
          plans: normalized.plans ?? [],
          artefacts: normalized.artefacts ?? [],
        } as WorkspaceSessionDetail;
      },
      providesTags: ['Workspace'],
    }),

    getArtefact: builder.query<WorkspaceArtefact, string>({
      query: (id) => `/workspace/artefacts/${id}`,
      transformResponse: (raw: unknown) =>
        normalizeKeys<WorkspaceArtefact>(raw),
    }),

    shareArtefact: builder.mutation<WorkspaceArtefact, ShareArtefactRequest>({
      query: ({ id, shareScope }) => ({
        url: `/workspace/artefacts/${id}/share`,
        method: 'POST',
        body: { share_scope: shareScope },
      }),
      transformResponse: (raw: unknown) =>
        normalizeKeys<WorkspaceArtefact>(raw),
    }),

    stopWorkspaceSession: builder.mutation<{ sessionId: string; status: string }, string>({
      query: (sessionId) => ({
        url: `/workspace/sessions/${sessionId}/stop`,
        method: 'POST',
      }),
      transformResponse: (raw: unknown) =>
        normalizeKeys<{ sessionId: string; status: string }>(raw),
      invalidatesTags: ['Workspace'],
    }),
  }),
});

export const {
  useSendWorkspaceMessageMutation,
  useGetProjectsQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
  useGetWorkspaceSessionsQuery,
  useGetSessionDetailQuery,
  useGetArtefactQuery,
  useShareArtefactMutation,
  useStopWorkspaceSessionMutation,
} = workspaceApi;
